import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import CookieConsent from "../components/CookieConsent";
import { fontStack, ensureFontLoaded, DEFAULT_FONT } from "../landingFonts";
import { setPageMeta, setJsonLd, setOpenGraph, demoteHeadings, cleanHtml, addImageAlts } from "../seo";
import api from "../api";
import { HOME_META } from "../pageMeta";
import { sectionTitle } from "../seoDefaults";
import { getPrerender } from "../prerender";
import { SiteHeader } from "../components/site/SiteHeader";
import { SiteFooter } from "../components/site/SiteFooter";
import { useSiteNav, visibleSectionKeys, SECTION_ANCHORS } from "../components/site/useSiteNav";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Clock, HeartHandshake, Car, Megaphone, GraduationCap, Plane,
  ArrowRight, Loader2, Sparkles, CheckCircle2, Send,
  Mail, Phone, MapPin, Users, Handshake, Briefcase,
} from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL || "";
const mediaUrl = (u) => (!u ? "" : u.startsWith("http") ? u : `${BASE}${u}`);

const CONTENT_KEYS = ["chisiamo", "lavora", "partners", "contatti"];

const RATIO_CLASS = { "16:9": "aspect-video", "4:3": "aspect-[4/3]", "1:1": "aspect-square" };

// Blocco immagine + testo con impaginazione configurabile dal Superadmin:
// posizione (sinistra/destra), adattamento (contain "intera" / cover "riempi"), rapporto.
const SectionImageText = ({ data, sk, cta }) => {
  const hasImg = !!data.image_url;
  const pos = data.image_position === "left" ? "left" : "right";
  const contain = data.image_fit !== "cover";
  const ratioCls = RATIO_CLASS[data.image_ratio];
  const objectCls = contain ? "object-contain" : "object-cover";
  const img = hasImg && (
    ratioCls ? (
      <div className={`w-full ${ratioCls} rounded-2xl overflow-hidden border border-white/10 ${contain ? "bg-white/[0.03]" : ""}`}>
        <img src={mediaUrl(data.image_url)} alt={data.image_alt || data.title || ""} data-testid={`${sk}-image`} className={`w-full h-full ${objectCls}`} />
      </div>
    ) : (
      <img src={mediaUrl(data.image_url)} alt={data.image_alt || data.title || ""} data-testid={`${sk}-image`}
        className={`w-full h-auto max-h-[440px] rounded-2xl border border-white/10 ${objectCls}`} />
    )
  );
  const bodyIsHtml = typeof data.body === "string" && /<[a-z][\s\S]*>/i.test(data.body);
  const text = (
    <div>
      {bodyIsHtml
        ? <div className="custom-page-content text-gray-300" data-testid={`${sk}-body`} dangerouslySetInnerHTML={{ __html: addImageAlts(demoteHeadings(cleanHtml(data.body)), sectionTitle(sk, data.title)) }} />
        : <p className="text-base text-gray-300 leading-relaxed whitespace-pre-line" data-testid={`${sk}-body`}>{data.body}</p>}
      {cta}
    </div>
  );
  if (!hasImg) return text;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
      {pos === "left" ? <>{img}{text}</> : <>{text}{img}</>}
    </div>
  );
};

const FEATURES = [
  { icon: Clock, title: "Gestione presenze", desc: "Timbrature e prospetti con export in file compatibili con i principali gestionali paghe." },
  { icon: HeartHandshake, title: "Gestione welfare", desc: "Convenzioni, benefit e piani welfare per i tuoi dipendenti, sempre a portata di mano." },
  { icon: Car, title: "Parco auto aziendale", desc: "Assegnazioni, scadenze e chilometri delle vetture aziendali con reportistica ESG." },
  { icon: Megaphone, title: "Comunicazioni e circolari", desc: "Bacheca aziendale per comunicazioni e circolari, notificate in tempo reale." },
  { icon: GraduationCap, title: "Gestione formazione", desc: "Corsi, attestati e percorsi formativi tracciati per ogni collaboratore." },
  { icon: Plane, title: "Gestione trasferte", desc: "Richieste, approvazioni e note spese delle trasferte con flusso digitale." },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const { startDemo } = useAuth();
  const [demoLoading, setDemoLoading] = useState("");
  const [quoteOpen, setQuoteOpen] = useState(false);
  const location = useLocation();
  const nav = useSiteNav();
  const [pre] = useState(() => getPrerender("/")?.landing || null);
  const [c, setC] = useState(pre?.sections || null);
  const [layout, setLayout] = useState(pre?.layout || null);
  const [font, setFont] = useState(pre?.font || DEFAULT_FONT);
  const [fontHeading, setFontHeading] = useState(pre?.font_heading || pre?.font || DEFAULT_FONT);
  const isPreview = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("preview") === "1";

  useEffect(() => {
    if (isPreview) {
      try {
        const draft = JSON.parse(localStorage.getItem("jobtimehr_landing_preview") || "null");
        if (draft && draft.sections) {
          setC(draft.sections);
          setLayout(draft.layout || null);
          setFont(draft.font || DEFAULT_FONT);
          setFontHeading(draft.font_heading || draft.font || DEFAULT_FONT);
          return;
        }
      } catch (e) { console.error("Anteprima landing non disponibile:", e); }
    }
    api.get("/landing-content").then((r) => {
      setC(r.data.sections); setLayout(r.data.layout || null);
      setFont(r.data.font || DEFAULT_FONT); setFontHeading(r.data.font_heading || r.data.font || DEFAULT_FONT);
    }).catch((e) => { console.error("Caricamento contenuti landing non riuscito:", e); setC(null); });
  }, [isPreview]);

  useEffect(() => { ensureFontLoaded(font); }, [font]);
  useEffect(() => { ensureFontLoaded(fontHeading); }, [fontHeading]);

  useEffect(() => {
    const id = decodeURIComponent((location.hash || "").slice(1));
    if (!id || !c) return;
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ block: "start" });
  }, [location.hash, c]);

  useEffect(() => {
    if (isPreview) return;
    const m = HOME_META;
    setPageMeta({ title: m.title, description: m.description, canonical: m.canonical });
    setOpenGraph({ title: m.title, description: m.description, image: m.image, url: m.canonical, type: "website" });
    setJsonLd("ld-org", m.jsonLd["ld-org"]);
    return () => setJsonLd("ld-org", null);
  }, [isPreview]);

  const launchDemo = async (role = "admin") => {
    setDemoLoading(role);
    const res = await startDemo(role);
    setDemoLoading("");
    if (res.ok) { toast.success("Modalità Demo avviata"); navigate("/", { replace: true }); }
    else toast.error(res.error || "Impossibile avviare la demo");
  };

  const home = c?.home || {};
  const chisiamo = c?.chisiamo || {};
  const lavora = c?.lavora || {};
  const partners = c?.partners || {};
  const contatti = c?.contatti || {};

  const visibleSecs = (layout && layout.length ? layout : CONTENT_KEYS.map((k) => ({ key: k, visible: true }))).filter((l) => l.visible);
  const sectionKeys = visibleSectionKeys(layout);

  const renderSection = (sk, idx) => {
    const alt = idx % 2 === 1;
    if (sk === "chisiamo") return (
      <Section key={sk} id={SECTION_ANCHORS.chisiamo} icon={Users} eyebrow="Chi siamo" title={sectionTitle("chisiamo", chisiamo.title)} alt={alt}>
        <SectionImageText data={chisiamo} sk="chisiamo" />
      </Section>
    );
    if (sk === "lavora") return (
      <Section key={sk} id={SECTION_ANCHORS.lavora} icon={Briefcase} eyebrow="Carriere" title={sectionTitle("lavora", lavora.title)} alt={alt}>
        <SectionImageText data={lavora} sk="lavora" cta={
          <button onClick={() => setQuoteOpen(true)} data-testid="lavora-cta"
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#ff7e00] hover:bg-[#e56f00] text-white font-bold text-[14px] px-6 py-3 transition-colors">
            <Send size={16} /> Invia la tua candidatura
          </button>
        } />
      </Section>
    );
    if (sk === "partners") return (
      <Section key={sk} id={SECTION_ANCHORS.partners} icon={Handshake} eyebrow="Partner" title={sectionTitle("partners", partners.title)} alt={alt}>
        <SectionImageText data={partners} sk="partners" />
        {Array.isArray(partners.items) && partners.items.length > 0 && (
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="partners-grid">
            {partners.items.map((p, i) => (
              <a key={p._id || `partner-${i}`} href={p.url || undefined} target={p.url ? "_blank" : undefined} rel="noreferrer"
                data-testid={`partner-${i}`}
                className="flex flex-col items-center justify-center gap-2 rounded-2xl bg-white/[0.05] border border-white/10 hover:border-[#ff7e00]/40 p-5 transition-colors">
                {p.logo_url
                  ? <img src={mediaUrl(p.logo_url)} alt={p.name ? `Logo ${p.name}` : "Logo partner"} className="h-12 w-auto object-contain" />
                  : <Handshake size={28} className="text-[#ff7e00]" />}
                {p.name && <span className="text-[13px] text-gray-300 text-center">{p.name}</span>}
              </a>
            ))}
          </div>
        )}
      </Section>
    );
    if (sk === "contatti") return (
      <Section key={sk} id={SECTION_ANCHORS.contatti} icon={Mail} eyebrow="Contatti" title={sectionTitle("contatti", contatti.title)} alt={alt}>
        <SectionImageText data={contatti} sk="contatti" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mt-8">
          <div>
            <div className="space-y-3">
              {contatti.email && <a href={`mailto:${contatti.email}`} data-testid="contatti-email" className="flex items-center gap-3 text-gray-200 hover:text-[#ff7e00] transition-colors"><span className="w-9 h-9 rounded-lg bg-[#ff7e00]/15 text-[#ff7e00] inline-flex items-center justify-center"><Mail size={17} /></span>{contatti.email}</a>}
              {contatti.phone && <a href={`tel:${contatti.phone}`} data-testid="contatti-phone" className="flex items-center gap-3 text-gray-200 hover:text-[#ff7e00] transition-colors"><span className="w-9 h-9 rounded-lg bg-[#ff7e00]/15 text-[#ff7e00] inline-flex items-center justify-center"><Phone size={17} /></span>{contatti.phone}</a>}
              {contatti.address && <div data-testid="contatti-address" className="flex items-start gap-3 text-gray-200"><span className="w-9 h-9 rounded-lg bg-[#ff7e00]/15 text-[#ff7e00] inline-flex items-center justify-center shrink-0"><MapPin size={17} /></span><span className="pt-1.5">{contatti.address}</span></div>}
            </div>
          </div>
          <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-6">
            <h3 className="text-lg font-black mb-1">Richiedi un preventivo</h3>
            <p className="text-[13px] text-gray-400 mb-4">Raccontaci della tua realtà: ti prepariamo una proposta su misura.</p>
            <button onClick={() => setQuoteOpen(true)} data-testid="contatti-cta-quote"
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#ff7e00] hover:bg-[#e56f00] text-white font-bold text-[14px] px-5 py-3 transition-colors">
              <Send size={16} /> Compila il modulo
            </button>
          </div>
        </div>
      </Section>
    );
    return null;
  };

  return (
    <div className="landing-scope min-h-screen bg-[#0d1b2f] text-white overflow-x-hidden" data-testid="landing-page" style={{ fontFamily: fontStack(font) }}>
      <style>{`.landing-scope h1,.landing-scope h2,.landing-scope h3{font-family:${fontStack(fontHeading)};}`}</style>
      {isPreview && (
        <div className="sticky top-0 z-[60] bg-[#ff7e00] text-white text-center text-[13px] font-bold py-2 px-4" data-testid="landing-preview-banner">
          Anteprima — modifiche non ancora pubblicate. Chiudi questa scheda per tornare all'editor.
        </div>
      )}
      <SiteHeader pages={nav.pages} sections={sectionKeys} onHome />

      {/* HOME / HERO */}
      <section id="home" className="relative scroll-mt-20">
        <div className="pointer-events-none absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-[#ff7e00]/20 blur-3xl" />
        <div className="pointer-events-none absolute top-40 -left-32 w-[360px] h-[360px] rounded-full bg-[#16345c]/60 blur-3xl" />
        <div className="relative max-w-4xl mx-auto px-6 pt-20 pb-16 text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/15 px-4 py-1.5 text-[12px] font-semibold text-gray-300 mb-8">
            <Sparkles size={14} className="text-[#ff7e00]" /> Portale HR per consulenti del lavoro e imprese
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.08] tracking-tight" data-testid="hero-title">
            {home.title || "La piattaforma HR cucita su misura del consulente del lavoro"}
          </h1>
          <p className="text-base md:text-lg text-gray-300 mt-7 max-w-2xl mx-auto leading-relaxed" data-testid="hero-subtitle">
            {home.subtitle || ""}
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <button onClick={() => launchDemo("admin")} disabled={!!demoLoading} data-testid="landing-cta-demo"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#ff7e00] hover:bg-[#e56f00] disabled:opacity-60 text-white font-bold text-[15px] px-7 py-4 shadow-lg shadow-[#ff7e00]/25 transition-colors w-full sm:w-auto">
              {demoLoading === "admin" ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
              Prova la Demo
              <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button onClick={() => setQuoteOpen(true)} data-testid="landing-cta-quote"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/25 text-white font-bold text-[15px] px-7 py-4 transition-colors w-full sm:w-auto">
              <Send size={17} /> Richiedi un preventivo
            </button>
          </div>
          <button onClick={() => launchDemo("employee")} disabled={!!demoLoading} data-testid="landing-demo-employee"
            className="mt-4 text-[13px] text-gray-400 hover:text-[#ff7e00] transition-colors underline underline-offset-4">
            {demoLoading === "employee" ? "Avvio in corso…" : "oppure entra nella demo come dipendente"}
          </button>
        </div>
        {/* FEATURES */}
        <div className="max-w-6xl mx-auto px-6 pb-8" data-testid="landing-features">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map(({ icon: Icon, title, desc }, i) => (
              <div key={title} data-testid={`feature-${i}`}
                className="group rounded-2xl bg-white/[0.04] hover:bg-white/[0.07] border border-white/10 hover:border-[#ff7e00]/40 p-6 transition-all duration-200">
                <span className="inline-flex w-12 h-12 items-center justify-center rounded-xl bg-[#ff7e00]/15 text-[#ff7e00] mb-4 group-hover:scale-105 transition-transform"><Icon size={22} /></span>
                <h3 className="text-lg font-black mb-1.5">{title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEZIONI CONTENUTO (ordine e visibilità gestiti dal Superadmin) */}
      {visibleSecs.map((l, idx) => renderSection(l.key, idx))}

      <SiteFooter pages={nav.pages} onHome />

      <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} />
      <CookieConsent />
    </div>
  );
};

const Section = ({ id, icon: Icon, eyebrow, title, children, alt = false }) => (
  <section id={id} className={`scroll-mt-20 ${alt ? "bg-white/[0.02]" : ""} border-t border-white/5`}>
    <div className="max-w-6xl mx-auto px-6 py-16">
      <div className="flex items-center gap-2 mb-2">
        <Icon size={18} className="text-[#ff7e00]" />
        <span className="text-[13px] font-black tracking-widest uppercase text-[#ffb066]">{eyebrow}</span>
      </div>
      <h2 className="text-3xl sm:text-4xl font-black mb-8">{title}</h2>
      {children}
    </div>
  </section>
);

const QuoteDialog = ({ open, onOpenChange }) => {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.company.trim()) { toast.error("Compila nome, email e nome azienda"); return; }
    setSending(true);
    try { await api.post("/quote-request", form); setDone(true); toast.success("Richiesta inviata! Ti ricontatteremo a breve."); }
    catch (err) { toast.error(err?.response?.data?.detail?.toString?.() || "Invio non riuscito. Riprova."); }
    finally { setSending(false); }
  };
  const close = (v) => { onOpenChange(v); if (!v) setTimeout(() => { setDone(false); setForm({ name: "", email: "", company: "", message: "" }); }, 250); };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-[480px]" data-testid="quote-dialog">
        <DialogHeader>
          <DialogTitle>Richiedi un preventivo</DialogTitle>
          <DialogDescription>Raccontaci della tua realtà: ti prepariamo una proposta su misura.</DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="py-8 text-center" data-testid="quote-success">
            <CheckCircle2 size={44} className="mx-auto text-emerald-500 mb-3" />
            <p className="font-bold text-[15px]">Richiesta inviata!</p>
            <p className="text-sm text-gray-500 mt-1">Ti ricontatteremo il prima possibile all'indirizzo indicato.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 pt-1">
            <div className="space-y-1.5"><Label htmlFor="q-name">Nome e cognome</Label><Input id="q-name" data-testid="quote-name" value={form.name} onChange={set("name")} placeholder="Mario Rossi" /></div>
            <div className="space-y-1.5"><Label htmlFor="q-email">Email</Label><Input id="q-email" type="email" data-testid="quote-email" value={form.email} onChange={set("email")} placeholder="mario@azienda.it" /></div>
            <div className="space-y-1.5"><Label htmlFor="q-company">Nome azienda</Label><Input id="q-company" data-testid="quote-company" value={form.company} onChange={set("company")} placeholder="Azienda S.r.l." /></div>
            <div className="space-y-1.5"><Label htmlFor="q-message">Messaggio</Label><Textarea id="q-message" data-testid="quote-message" value={form.message} onChange={set("message")} rows={4} placeholder="Numero di dipendenti, esigenze specifiche…" /></div>
            <button type="submit" disabled={sending} data-testid="quote-submit"
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-[#ff7e00] hover:bg-[#e56f00] disabled:opacity-60 text-white font-bold text-[14px] px-5 py-3 transition-colors">
              {sending ? <Loader2 size={17} className="animate-spin" /> : <Send size={16} />} Invia richiesta
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default LandingPage;
